import * as React from "react";
import { AlertTriangle } from "lucide-react";
import { displayAge } from "@/lib/patientAge";
import { cn } from "@/lib/utils";
import { PAGE_ACTIONS_CLASS } from "@/components/ui/page-header";

export type BannerPatient = {
  name: string;
  age?: number | string | null;
  date_of_birth?: string | null;
  gender?: string | null;
  blood_group?: string | null;
  mrn?: string | null;
  phone?: string | null;
  city?: string | null;
  allergies?: string | null;
  chronic_conditions?: string | null;
  current_medications?: string | null;
};

interface PatientBannerProps {
  patient: BannerPatient;
  /** "full" on patient pages, "compact" while prescribing */
  size?: "full" | "compact";
  /** Extra items for the identifiers line (e.g. "6 visits", a "View past" link, a date) */
  meta?: React.ReactNode;
  /** Amount due in rupees; shown as an amber chip when > 0 */
  outstanding?: number;
  /** Advance held in rupees (deposits not yet used); a neutral chip beside the due, as in the New bill header */
  advance?: number;
  /** Before the avatar — typically a Back button */
  leading?: React.ReactNode;
  /** Right-hand actions */
  actions?: React.ReactNode;
  className?: string;
}

/** "9970087263" → "99700 87263" for reading aloud or dialling; anything else is shown as stored */
export const groupPhone = (phone: string) => {
  const d = phone.replace(/\D/g, "");
  return d.length === 10 ? `${d.slice(0, 5)} ${d.slice(5)}` : phone;
};

const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).map(w => w[0]).join("").slice(0, 2).toUpperCase() || "?";

/**
 * Who the patient is, and what must not be missed about them — shown on every patient screen.
 * Allergies are red and always visible; when none are recorded that is said, not left blank.
 */
export function PatientBanner({ patient, size = "full", meta, outstanding, advance, leading, actions, className }: PatientBannerProps) {
  const compact = size === "compact";
  const age = displayAge(typeof patient.age === "string" ? Number(patient.age) || null : patient.age, patient.date_of_birth);
  const demographics = [age, patient.gender, patient.blood_group].filter(Boolean).join(" · ");
  const identifiers: React.ReactNode[] = [];
  if (patient.mrn) identifiers.push(<span key="mrn">MRN <span className="font-mono text-foreground/80">{patient.mrn}</span></span>);
  if (patient.phone) identifiers.push(<span key="phone" className="tabular-nums">{groupPhone(patient.phone)}</span>);
  if (patient.city && !compact) identifiers.push(<span key="city" className="capitalize">{patient.city}</span>);

  return (
    <div className={cn("flex flex-wrap items-center gap-x-4 gap-y-2 px-4 sm:px-6", compact ? "py-2" : "py-3", className)}>
      {leading}

      <div className="flex items-center gap-3 min-w-0">
        <span
          aria-hidden
          className={cn(
            "flex-shrink-0 rounded-full bg-primary/10 text-primary font-semibold flex items-center justify-center",
            compact ? "w-9 h-9 text-xs" : "w-11 h-11 text-sm"
          )}
        >
          {initials(patient.name)}
        </span>
        <div className="min-w-0">
          <div className="flex items-baseline gap-x-3 gap-y-0.5 flex-wrap">
            <h1 className={cn("font-semibold text-foreground truncate capitalize", compact ? "text-base" : "text-[22px] leading-7 tracking-[-0.01em]")}>
              {patient.name}
            </h1>
            {demographics && <span className="text-sm text-muted-foreground whitespace-nowrap">{demographics}</span>}
          </div>
          {(identifiers.length > 0 || meta) && (
            <div className={cn("flex items-center gap-x-2 text-muted-foreground flex-wrap", compact ? "text-xs" : "text-[13px]")}>
              {identifiers.map((node, i) => (
                <React.Fragment key={i}>{i > 0 && <span aria-hidden>·</span>}{node}</React.Fragment>
              ))}
              {meta && <>{identifiers.length > 0 && <span aria-hidden>·</span>}{meta}</>}
            </div>
          )}
        </div>
      </div>

      <ul aria-label="Safety information" className="flex items-center gap-1.5 flex-wrap min-w-0">
        {patient.allergies ? (
          <li className="inline-flex items-center gap-1 max-w-[22rem] rounded border border-destructive/30 bg-destructive/10 px-2 py-0.5 text-xs font-semibold text-destructive"
              title={`Allergies: ${patient.allergies}`}>
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" aria-hidden />
            <span className="truncate">Allergy: {patient.allergies}</span>
          </li>
        ) : (
          <li className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">No allergies recorded</li>
        )}
        {patient.chronic_conditions && (
          <li className="max-w-[18rem] truncate rounded bg-muted px-2 py-0.5 text-xs font-medium text-foreground/80" title={patient.chronic_conditions}>
            {patient.chronic_conditions}
          </li>
        )}
        {patient.current_medications && (
          <li className="max-w-[18rem] truncate rounded bg-muted px-2 py-0.5 text-xs text-foreground/80" title={`Current medicines: ${patient.current_medications}`}>
            On: {patient.current_medications}
          </li>
        )}
        {outstanding != null && outstanding > 0 && (
          <li className="rounded bg-highlight px-2 py-0.5 text-xs font-medium text-highlight-foreground tabular-nums">
            ₹{outstanding.toLocaleString("en-IN")} due
          </li>
        )}
        {advance != null && advance > 0 && (
          <li className="rounded bg-muted px-2 py-0.5 text-xs text-foreground/80 tabular-nums">
            ₹{advance.toLocaleString("en-IN")} advance
          </li>
        )}
      </ul>

      {/* Full banner: the page's main actions at 36px / 14px, as in the mockup; compact keeps the small size */}
      {actions && (
        <div className={cn(
          // Phones: wrap within the banner instead of running off its edge
          "flex flex-wrap items-center gap-2 ml-auto flex-shrink-0 max-w-full",
          !compact && PAGE_ACTIONS_CLASS,
        )}>
          {actions}
        </div>
      )}
    </div>
  );
}
